import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ApiError,
  listConversations,
  listLoadMessages,
  markLoadMessagesRead,
  sendLoadMessage,
  type LoadConversationSummary,
  type LoadMessage
} from "./api";
import { useAuth } from "./auth";
import { useRealtime } from "./realtime";

type InboxFilter = "all" | "unread" | "enquiry" | "assigned" | "closed";

const keyFor = (item: Pick<LoadConversationSummary, "loadId" | "carrierCompanyId">) =>
  `${item.loadId}:${item.carrierCompanyId}`;

export function MessagesPage() {
  const auth = useAuth();
  const realtime = useRealtime();
  const company = auth.session!.companies[0]!;
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState<LoadConversationSummary[]>([]);
  const [messages, setMessages] = useState<LoadMessage[]>([]);
  const [filter, setFilter] = useState<InboxFilter>("all");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState("");
  const threadRef = useRef<HTMLDivElement>(null);
  const threadRequestRef = useRef(0);

  const selectedKey = params.get("thread") ?? "";
  const selected = conversations.find((item) => keyFor(item) === selectedKey);
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return conversations.filter((item) => {
      const statusMatches = filter === "all" || (filter === "unread" ? item.unread : item.threadStatus === filter);
      const textMatches = !query || [item.otherCompanyName,item.description,...item.routePostcodes,item.lastMessage.body].some((value) => value.toLowerCase().includes(query));
      return statusMatches && textMatches;
    });
  }, [conversations, filter, search]);

  async function refreshInbox(preferredKey?: string) {
    const loaded = await listConversations(company.companyId);
    setConversations(loaded);
    const requested = preferredKey ?? selectedKey;
    const next = loaded.find((item) => keyFor(item) === requested) ?? loaded[0];
    if (next && keyFor(next) !== selectedKey) setParams({ thread: keyFor(next) }, { replace: true });
  }

  async function openThread(item: LoadConversationSummary) {
    const key = keyFor(item);
    const requestId = ++threadRequestRef.current;
    setParams({ thread: key });
    setMessages([]);
    setLoadingThread(true);
    setError("");
    try {
      const loaded = await listLoadMessages(item.loadId, company.companyId, item.carrierCompanyId);
      if (requestId !== threadRequestRef.current) return;
      setMessages(loaded);
      await markLoadMessagesRead(item.loadId, company.companyId, item.carrierCompanyId);
      setConversations((current) => current.map((value) => keyFor(value) === key ? { ...value, unread: false } : value));
      window.dispatchEvent(new Event("loadsub:navigation-refresh"));
    } catch (caught) {
      if (requestId === threadRequestRef.current) setError(messageFor(caught));
    } finally {
      if (requestId === threadRequestRef.current) setLoadingThread(false);
    }
  }

  useEffect(() => {
    const selectedIsVisible = visible.some((item) => keyFor(item) === selectedKey);
    if (selectedIsVisible) return;
    threadRequestRef.current += 1;
    setMessages([]);
    setLoadingThread(false);
    const next = visible[0];
    setParams(next ? { thread: keyFor(next) } : {}, { replace: true });
  }, [filter, search, visible, selectedKey, setParams]);

  useEffect(() => {
    void refreshInbox().catch((caught) => setError(messageFor(caught)));
  }, [company.companyId]);

  useEffect(() => {
    if (selected) void openThread(selected);
  }, [selectedKey]);

  useEffect(() => {
    const event = realtime.latest;
    if (!event || event.type !== "load.message") return;
    const activeKey = selected ? keyFor(selected) : undefined;
    void refreshInbox(activeKey).then(() => {
      if (selected && event.loadId === selected.loadId && event.carrierCompanyId === selected.carrierCompanyId) {
        return openThread(selected);
      }
    }).catch((caught) => setError(messageFor(caught)));
  }, [realtime.latest?.id]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    });
    return () => cancelAnimationFrame(frame);
  }, [messages.length, selectedKey]);

  async function send(event: FormEvent) {
    event.preventDefault();
    if (!selected || !draft.trim() || !selected.canReply) return;
    setBusy(true);
    setError("");
    try {
      const sent = await sendLoadMessage(
        selected.loadId,
        company.companyId,
        draft,
        selected.poster ? selected.carrierCompanyId : undefined
      );
      setMessages((current) => [...current, sent]);
      setDraft("");
      await markLoadMessagesRead(selected.loadId, company.companyId, selected.carrierCompanyId);
      await refreshInbox(keyFor(selected));
      window.dispatchEvent(new Event("loadsub:navigation-refresh"));
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  return <>
    <header className="team-header">
      <div><h1 className="h3 mb-1">Messages</h1><p className="text-secondary mb-0">Private load enquiries and assigned-work conversations.</p></div>
    </header>
    {error && <div className="alert alert-danger py-2">{error}</div>}
    <nav className="message-inbox-filters" aria-label="Filter messages">
      {([["all","All"],["unread","Unread"],["enquiry","Enquiries"],["assigned","Assigned work"],["closed","Closed"]] as const).map(([value,label]) =>
        <button type="button" className={filter === value ? "active" : ""} onClick={() => setFilter(value)} aria-pressed={filter === value} key={value}>{label}</button>
      )}
    </nav>
    <label className="message-inbox-search"><span className="visually-hidden">Search conversations</span><input className="form-control" type="search" placeholder="Search company, route, load or message…" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
    <section className={`message-inbox${selected ? " has-selection" : ""}`}>
      <aside className="message-conversation-list" aria-label="Conversations">
        {visible.map((item) => <button type="button" className={`message-conversation${keyFor(item) === selectedKey ? " active" : ""}${item.unread ? " unread" : ""}`} onClick={() => void openThread(item)} key={keyFor(item)}>
          <span className="message-conversation-top"><strong>{item.otherCompanyName}</strong><time>{formatTime(item.lastMessage.createdAt)}</time></span>
          <span className="message-route">{item.routePostcodes.join(" → ")}</span>
          <span className="message-description">{item.description}</span>
          <span className="message-preview">{item.lastMessage.senderCompanyId === company.companyId ? "You" : item.lastMessage.senderTradingName}: {item.lastMessage.body}</span>
          <span className="message-thread-status">{labelStatus(item.threadStatus)}{item.unread && <b>Unread</b>}</span>
        </button>)}
        {visible.length === 0 && <div className="message-empty"><strong>No conversations</strong><span>{filter === "all" ? "Load enquiries will appear here." : "Nothing matches this filter."}</span></div>}
      </aside>
      <article className="message-thread-panel">
        {!selected ? <div className="message-empty"><strong>Select a conversation</strong><span>Choose a load thread to read or reply.</span></div> : <>
          <header>
            <button className="message-mobile-back" type="button" onClick={() => setParams({})}>← Conversations</button>
            <div><strong>{selected.otherCompanyName}</strong><span>{selected.routePostcodes.join(" → ")}</span><small>{selected.description}</small></div>
            <Link className="btn btn-sm btn-outline-light" to={selected.poster ? `/posted-loads?load=${selected.loadId}` : `/assigned-loads?load=${selected.loadId}`}>View load</Link>
          </header>
          <div className="message-thread-scroll" ref={threadRef}>
            {loadingThread ? <p className="text-secondary">Loading messages…</p> : messages.map((item) => <article className={item.senderCompanyId === company.companyId ? "message-own" : ""} key={item.id}>
              <div><strong>{item.senderCompanyId === company.companyId ? "You" : item.senderTradingName}</strong><time>{new Date(item.createdAt).toLocaleString("en-GB")}</time></div>
              <p>{item.body}</p>
            </article>)}
          </div>
          {selected.canReply ? <form className="message-inbox-compose" onSubmit={send}>
            <textarea className="form-control" rows={2} maxLength={2000} required placeholder="Write a reply…" value={draft} onChange={(event) => setDraft(event.target.value)} />
            <button className="btn btn-primary" disabled={busy || !draft.trim()}>{busy ? "Sending…" : "Send"}</button>
          </form> : <p className="message-closed-notice">This conversation is read-only because the load was assigned to another carrier.</p>}
        </>}
      </article>
    </section>
  </>;
}

function labelStatus(status: LoadConversationSummary["threadStatus"]) {
  return status === "assigned" ? "Assigned work" : status === "enquiry" ? "Enquiry" : "Closed";
}

function formatTime(value: string) {
  const date = new Date(value);
  const today = new Date();
  return date.toDateString() === today.toDateString()
    ? date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function messageFor(error: unknown) {
  return error instanceof ApiError ? error.message : "Unable to load messages. Please try again.";
}
